import React, { useEffect, useRef } from "react";
import { AlertCircle, LogOut, RefreshCw } from "lucide-react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./firebase/AuthProvider";
import { BrandLoader } from "./components/ui/Logo/BrandLoader";
import { WaiveraLogo } from "./components/ui/Logo/WaiveraLogo";
import { performanceNow, recordPerformanceTiming } from "./utilities/performanceMetrics";
import "./ProtectedRoute.css";

const ProtectedRoute = () => {
  const {
    currentUser,
    currentUserInfo,
    impersonatedUserId,
    isLoading,
    authInitializationError,
    authInitializationFailureStatus,
    retryAuthInitialization,
    logout,
    requiresPasswordChange,
    requiresOnboarding,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // ProtectedRoute survives the loading branch that unmounts its Outlet.
  // Keep focused signature links scoped here rather than only in that page.
  const signatureIdentity = JSON.stringify([
    currentUser?.uid,
    impersonatedUserId || "",
    currentUserInfo?.id,
    currentUserInfo?.organization?.id,
  ]);
  const previousSignatureIdentity = useRef<string | null>(null);
  const isSignatureHistory = location.pathname.replace(/\/+$/, "") === "/signatures";
  const hasFocusedSignature = isSignatureHistory && new URLSearchParams(location.search).has("requestId");
  const signatureIdentityChanged = previousSignatureIdentity.current !== null &&
    previousSignatureIdentity.current !== signatureIdentity;
  const discardFocusedSignature = !isLoading && !!currentUser && !authInitializationError &&
    signatureIdentityChanged && hasFocusedSignature;

  useEffect(() => {
    if (isLoading || !currentUser || authInitializationError) return;
    if (discardFocusedSignature) {
      const search = new URLSearchParams(location.search);
      search.delete("requestId");
      navigate({ pathname: location.pathname, search: search.toString(), hash: location.hash }, { replace: true });
    }
    previousSignatureIdentity.current = signatureIdentity;
  }, [isLoading, currentUser, authInitializationError, signatureIdentity, discardFocusedSignature,
      location.pathname, location.search, location.hash, navigate]);
  const mountedAtRef = useRef(performanceNow());
  const protectedContentRecordedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && currentUser && !protectedContentRecordedRef.current) {
      protectedContentRecordedRef.current = true;
      recordPerformanceTiming("auth.protected_content", mountedAtRef.current);
    }
  }, [currentUser, isLoading]);

  if (isLoading || discardFocusedSignature) {
    return <BrandLoader fullScreen />;
  }

  if (currentUser && authInitializationError) {
    return (
      <div className="auth-initialization-page">
        <main
          role="alert"
          aria-live="assertive"
          aria-labelledby="auth-initialization-error-title"
          className="auth-initialization-page__main"
        >
          <section className="auth-initialization-card">
            <div className="auth-initialization-card__accent" />

            <div className="auth-initialization-card__body">
              <div className="auth-initialization-card__header">
                <WaiveraLogo />
                <div className="auth-initialization-card__icon">
                  <AlertCircle aria-hidden="true" size={23} strokeWidth={2} />
                </div>
              </div>

              <div className="auth-initialization-card__copy">
                <p className="auth-initialization-card__eyebrow">
                  Sign-in issue
                </p>
                <h1
                  id="auth-initialization-error-title"
                  className="auth-initialization-card__title"
                >
                  We couldn't finish signing you in
                </h1>
                <p className="auth-initialization-card__description">
                  Your account data and permissions weren't loaded, so we haven't shown any protected content.
                </p>
              </div>

              {authInitializationFailureStatus != null && (
                <div className="auth-initialization-card__reference">
                  <span>Reference</span>
                  <code>
                    AUTH-{authInitializationFailureStatus}
                  </code>
                </div>
              )}

              <div className="auth-initialization-card__actions">
                <button
                  type="button"
                  onClick={() => void retryAuthInitialization()}
                  className="auth-initialization-card__retry-button"
                >
                  <RefreshCw aria-hidden="true" size={17} />
                  Retry
                </button>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="auth-initialization-card__signout-button"
                >
                  <LogOut aria-hidden="true" size={17} />
                  Sign out
                </button>
              </div>

              <p className="auth-initialization-card__hint">
                If this keeps happening, sign out and try signing in again.
              </p>
            </div>
          </section>
        </main>
      </div>
    );
  }

  else if (location.pathname.startsWith("/sign/")) {
    return <Outlet />;
  }

  else if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  else if (
    requiresPasswordChange === true &&
    location.pathname !== "/change-password"
  ) {
    return <Navigate to="/change-password" state={{ from: location }} replace />;
  }

  // --- NEW: KICK USER TO ONBOARDING IF REQUIRED ---
  else if (
    requiresOnboarding === true &&
    !location.pathname.startsWith("/edit/workflows") &&
    !location.pathname.startsWith("/settings/organization/security")
  ) {
    console.log("ProtectedRoute: User needs onboarding, redirecting.");
    return <Navigate to="/organization/onboarding" replace />;
  }

  // if (needsEmailVerification) return <Navigate to="/verify-email" />;
  // if (needsMfaEnrollment)    return <Navigate to="/mfa-setup" />;

  else return <Outlet />;
};

export default ProtectedRoute;
